import { appDatabasePrimaryFunctions, firebase } from '../../base';
import { Toast, ErrorMessage } from '../utils/HelpfulFunction';

let apiUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api/`;
if (process.env.NODE_ENV === 'development') {
    apiUrl = `http://localhost:5001/${process.env.REACT_APP_FIREBASE_PROJECT_ID}/us-central1/api/`;
}

/**
 * Get the current user's Firebase auth token for API calls.
 */
async function getAuthToken() {
    const user = firebase.auth().currentUser;
    if (!user) throw new Error('Not authenticated');
    return user.getIdToken();
}

/**
 * Send an immediate push notification to all registered devices.
 * Optional targeting: { experienceId, audience, team } — fields are only
 * included in the request when set, so untargeted sends behave exactly as before.
 */
export async function sendImmediateNotification(title, body, link, targeting = {}, imageUrl = '') {
    try {
        const token = await getAuthToken();
        const payload = { title, body, link, imageUrl: imageUrl || '' };
        if (targeting && typeof targeting.experienceId === 'string' && targeting.experienceId !== '__all__') {
            payload.experienceId = targeting.experienceId;
        }
        if (targeting && targeting.audience === 'stm' && targeting.team) {
            payload.audience = targeting.audience;
            payload.team = targeting.team;
        }
        const response = await fetch(`${apiUrl}sendNotification`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to send notification');
        }

        if (result.sent === 0 && result.message) {
            await Toast.fire({ title: result.message });
        } else {
            await Toast.fire({ title: 'Notification Successfully Sent!' });
        }
        return result;
    } catch (error) {
        console.error('Error sending immediate notification:', error);
        await ErrorMessage.fire({
            title: 'Error',
            text: error.message || 'Could not send push notification.'
        });
        return null;
    }
}

/**
 * Schedule a push notification for a future time.
 */
export async function scheduleNotification(title, body, link, scheduledTime, imageUrl = '', targeting = {}) {
    try {
        const token = await getAuthToken();
        const response = await fetch(`${apiUrl}scheduleNotification`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                title,
                body,
                link,
                imageUrl: imageUrl || '',
                // Omitted entirely when the admin picked nothing, so the server
                // stores null and the executor reads that as "no filter".
                ...targeting,
                scheduledTime: scheduledTime.toISOString()
            })
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to schedule notification');
        }

        await Toast.fire({ title: 'Notification Successfully Scheduled!' });
        return result;
    } catch (error) {
        console.error('Error scheduling notification:', error);
        await ErrorMessage.fire({
            title: 'Error',
            text: error.message || 'Could not schedule push notification.'
        });
        return null;
    }
}

/**
 * Delete (cancel) a scheduled push notification.
 */
export async function deleteScheduledNotification(overallPushId) {
    try {
        const token = await getAuthToken();
        const response = await fetch(`${apiUrl}scheduledNotification/${overallPushId}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to delete notification');
        }

        await Toast.fire({ title: 'Successfully Deleted Scheduled Notification' });
        return result;
    } catch (error) {
        console.error('Error deleting scheduled notification:', error);
        await ErrorMessage.fire({
            title: 'Error',
            text: error.message || 'Could not delete scheduled notification.'
        });
        return null;
    }
}

/**
 * Edit a scheduled push notification.
 */
export async function editScheduledNotification(overallPushId, title, body, link, scheduledTime, imageUrl = '') {
    try {
        const token = await getAuthToken();
        const response = await fetch(`${apiUrl}scheduledNotification/${overallPushId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
                title,
                body,
                link,
                imageUrl: imageUrl || '',
                scheduledTime: scheduledTime.toISOString()
            })
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to edit notification');
        }

        await Toast.fire({ title: 'Notification Successfully Edited!' });
        return result;
    } catch (error) {
        console.error('Error editing scheduled notification:', error);
        await ErrorMessage.fire({
            title: 'Error',
            text: error.message || 'Could not edit scheduled notification.'
        });
        return null;
    }
}

/**
 * Get push notification stats for a date range.
 */
export async function getPushStats(startMs, endMs) {
    try {
        const token = await getAuthToken();
        const response = await fetch(`${apiUrl}pushStats?start=${startMs}&end=${endMs}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to fetch stats');
        }

        return result;
    } catch (error) {
        console.error('Error fetching push stats:', error);
        return { sent: 0, failed: 0, opened: 0, delivered: 0, openRate: '0%', deliveredRate: '0%', byPlatform: null, notifications: [] };
    }
}

/**
 * Get all FCM tokens registered for a specific authenticated user (one per device).
 * Returns an array of token strings; empty array if no registered devices.
 */
export async function getPushTokensForUser(uid) {
    try {
        const authToken = await getAuthToken();
        const response = await fetch(
            `${apiUrl}fcmTokensForUser?uid=${encodeURIComponent(uid)}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                }
            }
        );
        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to fetch push tokens');
        }
        return Array.isArray(result.tokens) ? result.tokens : [];
    } catch (error) {
        console.error('Error fetching push tokens for user:', error);
        return [];
    }
}

/**
 * Look up a user by their email via the usernameToEmail index.
 * Returns { userName, uid, email, ... } or null if not found.
 */
export async function getUserByEmail(email) {
    if (!email) return null;
    try {
        const snap = await appDatabasePrimaryFunctions
            .ref('usernameToEmail')
            .orderByChild('email')
            .equalTo(email.trim())
            .limitToFirst(1)
            .once('value');
        const data = snap.val();
        if (!data) return null;
        const userName = Object.keys(data)[0];
        return { userName, ...data[userName] };
    } catch (error) {
        console.error('Error looking up user by email:', error);
        return null;
    }
}

/**
 * Send an immediate push notification to specific FCM tokens.
 */
export async function sendImmediateNotificationToTokens(tokens, title, body, link, imageUrl = '') {
    try {
        const authToken = await getAuthToken();
        const response = await fetch(`${apiUrl}sendNotificationToTokens`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({ tokens, title, body, link, imageUrl: imageUrl || '' })
        });

        const result = await response.json();
        if (!response.ok) {
            throw new Error(result.error || 'Failed to send notification');
        }
        if (result.sent === 0 && result.failed > 0) {
            throw new Error('All tokens failed to send. Check Cloud Functions logs for details.');
        }

        await Toast.fire({ title: 'Push Notification Sent!' });
        return result;
    } catch (error) {
        console.error('Error sending notification to tokens:', error);
        await ErrorMessage.fire({
            title: 'Error',
            text: error.message || 'Could not send push notification.'
        });
        return null;
    }
}

/**
 * Get all future scheduled push notifications from Firebase.
 */
export async function getAllFutureScheduledPushes() {
    try {
        const now = new Date().getTime();
        const scheduledPushesSnapshot = await appDatabasePrimaryFunctions
            .ref('scheduledPushes')
            .orderByChild('milliTime')
            .startAfter(now)
            .limitToFirst(100)
            .once('value');

        const results = [];
        scheduledPushesSnapshot.forEach((snap) => {
            const details = snap.val();
            if (details.status === 'pending') {
                results.push(details);
            }
        });
        return results;
    } catch (e) {
        console.error(e);
        return [];
    }
}
